import Typography from "@mui/material/Typography";
import InfoIcon from "@mui/icons-material/Info";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import { styled } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import Guid from "_shared/guids/guid";
import Stack from "@mui/material/Stack";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import Individual from "marketplace/individuals/individual";
import { useMemo } from "react";
import { useSession } from "users/__session/session-context";
import { Masked } from "work/masked-fields";
import VendorTeamReadOnly from "work/work-agreements/teams/vendor-team/_views/vendor-team-readonly-view";
import VendorTeamRestaffing from "work/work-agreements/teams/vendor-team/_views/vendor-team-restaffing-view";
import WorkAgreement from "work/work-agreements/work-agreement";
import { workPaneNarrowerThan } from "work/_views/work-pane-layout";

const ListContainer = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(2),
  height: "100%",
  marginLeft: theme.spacing(3),
  marginTop: theme.spacing(2),
  minHeight: "20rem",
}));
const InfoContainer = styled(Box)(({ theme }) => ({
  alignItems: "center",
  display: 'flex',
  gap: theme.spacing(1),
  flexDirection: "row",
  flexWrap: "nowrap",
}));
const TeamRestrictedText = styled(Typography)(({ theme }) => ({
  fontStyle: "italic",
  fontSize: "1.1rem",
  fontWeight: 500,
  color: theme.palette.text.secondary,
}));
const TeamReview = styled(VendorTeamReadOnly)(({ theme }) => ({
  [workPaneNarrowerThan(theme.breakpoints.values.lg)]: {
    marginTop: theme.spacing(2),
  },
  marginLeft: theme.spacing(2),
}));
const TeamRestaffing = styled(VendorTeamRestaffing)(({ theme }) => ({
  [theme.breakpoints.down("lg")]: {
    marginTop: theme.spacing(2),
  },
  marginLeft: theme.spacing(2),
}));

type TeamTabProps = {
  entityId: Guid;
  workAgreement: WorkAgreement;
};

export default function TeamTab(props: Readonly<TeamTabProps>) {
  const { workAgreement } = props;
  const session = useSession();

  const individualUserIds: Set<string> = useMemo(() => {
    const vendorTeam = workAgreement.vendorTeam;
    if (Masked.isMasked(vendorTeam) || !vendorTeam?.leader) return new Set<string>();

    return new Set([
      vendorTeam.leader.userId.value,
      ...vendorTeam.members.map((member) => member.userId.value),
    ]);
  }, [workAgreement.vendorTeam]);

  const individualQuery = useGetIndividualsByUserIdsQuery(
    individualUserIds.size ? { userIds: individualUserIds } : skipToken,
  );

  const individuals = useMemo(() => {
    if (!individualQuery.data) return [];
    return Individual.fromJSONArray(individualQuery.data) ?? [];
  }, [individualQuery.data]);

  const leader = individuals.find(
    (ind: Individual) =>
      Masked.isPresentNotMasked(workAgreement.vendorTeam) && ind.userId.isEqualTo(workAgreement.vendorTeam.leader?.userId),
  );

  const members = individuals.filter(
    (ind: Individual) =>
      Masked.isNotMasked(workAgreement.vendorTeam) && !ind.userId.isEqualTo(workAgreement.vendorTeam?.leader?.userId),
  );

  if (Masked.isMasked(workAgreement.vendorTeam) || Masked.isMasked(workAgreement.teamRestricted)) {
    return "You do not have permission to view this team.";
  }

  const vendorTeam = workAgreement.vendorTeam;
  /**
   * What the reader may do with this roster, or nothing when all they may do is read it.
   *
   * Three things have to hold together, and each of them is the server's rule rather than this
   * tab's. The roster is the VENDOR's own staffing, so only the person leading that team may change
   * it — the client's route to a different team is Revise, and `PUT work/{id}/vendor-team-members`
   * answers anybody else with a 403. It is staffing rather than a term only while
   * `vendorTeamRosterIsAnAgreedTerm` is false, which is the predicate the endpoint itself turns on.
   * And it is only worth changing on work that is actually running: the endpoint would take a
   * restricted roster on ended work in place, but nobody is staffed on ended work, so the tab reads
   * as the record it is.
   */
  const restaffing =
    vendorTeam &&
    workAgreement.id &&
    vendorTeam.leader?.userId.isEqualTo(session.user?.id) &&
    workAgreement.isActiveWork &&
    !workAgreement.vendorTeamRosterIsAnAgreedTerm
      ? { team: vendorTeam, workAgreementId: workAgreement.id }
      : undefined;

  return (
    <ListContainer>
      <InfoContainer>
        <Tooltip 
          title="If a team is restricted, re-negotiation is required to change the team members. 
          If not restricted, team members can be changed without re-negotiation."
          placement="bottom-start"
        >
          <InfoIcon color="info" />
        </Tooltip>
        <TeamRestrictedText>
          {workAgreement.teamRestricted.value ? "Team is restricted" : "Team is not restricted"}
        </TeamRestrictedText>
      </InfoContainer>
      {restaffing ? (
        <TeamRestaffing
          workAgreementId={restaffing.workAgreementId}
          team={restaffing.team}
          individuals={individuals}
        />
      ) : (
        <TeamReview leader={leader ?? null} members={members} />
      )}
    </ListContainer>
  );
}
